import clsx from 'clsx';
import { useFormField } from 'components/common/Form/FormFieldContext';
import {
  CSSProperties,
  KeyboardEvent,
  ReactNode,
  SyntheticEvent,
  useId,
} from 'react';

import styles from './Checkbox.module.css';

interface CheckboxChangeData {
  checked: boolean;
  value?: string;
}

interface CheckboxProps {
  id?: string;
  name?: string;
  value?: string;
  className?: string;
  style?: CSSProperties;
  label?: ReactNode;
  checked?: boolean;
  disabled?: boolean;
  /** Render as a round radio button rather than a square checkbox */
  radio?: boolean;
  onChange?: (e: SyntheticEvent<HTMLElement>, data: CheckboxChangeData) => void;
}

/**
 * An AI-generated replacement for SemanticUI checkboxes. We probably want more refined styling in the future
 *
 * The whole component is the click target, rather than relying on a <label> to forward clicks to the input. That way a
 * click only triggers one click event, which matters for parents that also toggle state in their own onClick (e.g.
 * InterestsSelector).
 */
const Checkbox = ({
  id,
  name,
  value,
  className,
  style,
  label,
  checked = false,
  disabled,
  radio,
  onChange,
}: CheckboxProps) => {
  const labelId = useId();
  const { error } = useFormField();

  const toggle = (e: SyntheticEvent<HTMLElement>) => {
    if (disabled) {
      return;
    }
    // A radio button can't be unchecked by clicking it again
    if (radio && checked) {
      return;
    }
    onChange?.(e, { checked: !checked, value });
  };

  return (
    <div
      className={clsx(
        styles.checkbox,
        radio && styles.radio,
        checked && styles.checked,
        disabled && styles.disabled,
        error && styles.error,
        !label && styles.noLabel,
        className,
      )}
      style={style}
      onClick={toggle}
    >
      <input
        id={id}
        name={name}
        value={value}
        type={radio ? 'radio' : 'checkbox'}
        className={styles.input}
        checked={checked}
        disabled={disabled}
        aria-labelledby={label ? labelId : undefined}
        // State changes are handled by the click handler above
        onChange={() => {}}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
          // Browsers fire a click for the space key already, but not for enter
          if (e.key === 'Enter') {
            e.preventDefault();
            toggle(e);
          }
        }}
      />
      <span className={styles.box} aria-hidden="true" />
      {label && (
        <span id={labelId} className={styles.label}>
          {label}
        </span>
      )}
    </div>
  );
};

export default Checkbox;
